import { useState } from 'react'

import { Box, Stack, Typography } from '@mui/material'
import { LockIcon } from 'lucide-react'

import { Button, Card, Skeleton } from '@repo/ui'
import { EmptyState } from 'components'
import { useSubscriptionPlans } from 'hooks/api'
import { billingApi } from 'services'
import { toDollar } from 'utils'
import { vhMinus } from 'utils/textZoom'

/**
 * VRG-361 Slice 2 step 4 — the demo console trial's "you're bounced" surface.
 *
 * Reached via the axios response interceptor in `services/index.ts`, which redirects
 * here on any 403 carrying `code: 'CONSOLE_TRIAL_EXPIRED'` (AssertConsoleTrialActiveUseCase
 * on the API). The account is NOT locked out of the product forever — the console access
 * is gated, the company's data and certificates are untouched, and paying restores access
 * immediately (AssertConsoleTrialActiveUseCase treats an active subscription as
 * overriding an expired trial regardless of this page).
 *
 * Deliberately a STANDALONE page, not nested inside the normal sidebar Layout: almost
 * every other company-scoped endpoint now 403s with the same code, so the one surface
 * that MUST render reliably cannot depend on the rest of the shell succeeding. It only
 * calls two endpoints, both exempted from the trial lock on the API
 * (`@BypassTrialLock()`): listing plans (already public) and creating the Stripe
 * checkout session.
 */
export const TrialEndedPage = () => {
  const { data: plans, isLoading, isError } = useSubscriptionPlans()
  const [startingCheckoutFor, setStartingCheckoutFor] = useState<string | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  const handleSubscribe = async (priceId: string) => {
    setCheckoutError(null)
    setStartingCheckoutFor(priceId)
    try {
      const { checkoutUrl } = await billingApi.createSubscriptionCheckoutLink(priceId)
      window.location.href = checkoutUrl
    } catch {
      setCheckoutError(
        'We could not start checkout. Please try again, or contact support if this continues.',
      )
      setStartingCheckoutFor(null)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 6,
        height: vhMinus(),
        minHeight: vhMinus(),
        overflowY: 'auto',
      }}
    >
      <EmptyState
        icon={<LockIcon strokeWidth={1} size={32} />}
        title="Your trial has ended"
        text="Subscribe to keep using the Vera console. Your data and certificates are safe and untouched — nothing is lost."
        sx={{ pb: 4 }}
      />

      <Box sx={{ width: '100%', maxWidth: 720 }}>
        {isLoading && (
          <Stack direction="row" spacing={3}>
            {[0, 1].map(i => (
              <Card key={i} sx={{ flex: 1 }}>
                <Stack spacing={2}>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={32} />
                  <Skeleton variant="rounded" height={40} />
                </Stack>
              </Card>
            ))}
          </Stack>
        )}

        {isError && (
          <Typography variant="body3" color="error.main" textAlign="center">
            We could not load subscription plans right now. Please try again, or contact
            support if this continues.
          </Typography>
        )}

        {!isLoading && !isError && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            {(plans ?? [])
              .filter(plan => plan.active)
              .map(plan => (
                <Card key={plan.id} sx={{ flex: 1 }}>
                  <Stack spacing={2}>
                    <Typography variant="body2" fontWeight={600}>
                      {plan.title}
                    </Typography>
                    <Typography variant="h5">
                      {toDollar(plan.amount / 100, true)}
                      <Typography component="span" variant="body3" color="text.secondary">
                        {' '}
                        / {plan.interval}
                      </Typography>
                    </Typography>
                    <Button
                      variant="primary"
                      fullWidth
                      disableElevation
                      disabled={startingCheckoutFor !== null}
                      onClick={() => handleSubscribe(plan.stripe_price_id)}
                    >
                      {startingCheckoutFor === plan.stripe_price_id ? 'Redirecting…' : 'Subscribe'}
                    </Button>
                  </Stack>
                </Card>
              ))}
          </Stack>
        )}

        {checkoutError && (
          <Typography variant="body3" color="error.main" textAlign="center" sx={{ mt: 3 }}>
            {checkoutError}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
